import { FC, useContext, useState, useRef } from "react";
import { Select, SelectProps, Button } from "antd";
import { useTranslation } from "react-i18next";
import { DictContext } from "@/layout/context/DictContext";
import { Org } from "@/types/org";

interface SelectOrgsProps {
  defaultValues?: string[];
  onConfirm?: (orgs: Org[]) => Promise<void>;
  disabled?: boolean;
  placeholder?: string;
}

const SelectOrgs: FC<SelectOrgsProps> = ({
  defaultValues = [],
  onConfirm,
  disabled = false,
  placeholder,
}) => {
  const { t } = useTranslation();
  const { orgs } = useContext(DictContext);
  const [open, setOpen] = useState(false);
  const [tempValues, setTempValues] = useState<string[]>(defaultValues);
  const internalChangeRef = useRef(false);

  const handleDropdownVisibleChange = (newOpen: boolean) => {
    // 内部控制关闭，允许通过按钮关闭
    if (internalChangeRef.current) {
      internalChangeRef.current = false;
      setOpen(newOpen);
    } else {
      // 外部触发，只允许打开，不允许关闭
      if (newOpen) {
        setOpen(true);
        setTempValues(defaultValues);
      }
    }
  };

  const handleConfirm = () => {
    const selectedOrgs = orgs.filter((org) => tempValues.includes(org.id));
    onConfirm && onConfirm(selectedOrgs)
      .catch(() => setTempValues(defaultValues));
    internalChangeRef.current = true;
    setOpen(false);
  };

  const handleCancel = () => {
    setTempValues(defaultValues);
    internalChangeRef.current = true;
    setOpen(false);
  };

  const handleChange = (newValue: string | string[]) => {
    const newValues = Array.isArray(newValue) ? (newValue as string[]) : [newValue];

    // 如果下拉框未打开，说明是点击 tag 关闭触发的，直接触发 onConfirm
    if (!open && onConfirm) {
      const selectedOrgs = orgs.filter((org) => newValues.includes(org.id));
      onConfirm(selectedOrgs).catch(() => setTempValues(defaultValues));
    }
    setTempValues(newValues);
  };

  const dropdownRender: SelectProps["dropdownRender"] = (menu) => (
    <>
      {menu}
      <div style={{ padding: "8px", borderTop: "1px solid #eee", display: "flex", gap: "8px", justifyContent: "flex-end" }}>
        <Button size="small" onClick={handleCancel}>
          {t("common.cancel")}
        </Button>
        <Button size="small" type="primary" onClick={handleConfirm}>
          {t("common.confirm")}
        </Button>
      </div>
    </>
  );

  return (
    <Select
      open={open}
      onDropdownVisibleChange={handleDropdownVisibleChange}
      showSearch
      style={{ width: "100%" }}
      value={tempValues}
      placeholder={placeholder ?? t("org.selectOrg")}
      onChange={handleChange}
      options={orgs.map((org) => ({
        value: org.id,
        label: org.name,
      }))}
      mode="multiple"
      disabled={disabled}
      dropdownRender={dropdownRender}
    />
  );
};

export default SelectOrgs;
